import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PencilLine, Save, Plus } from "lucide-react";

const FY_MONTHS = ["Apr-26", "May-26", "Jun-26", "Jul-26", "Aug-26", "Sep-26", "Oct-26", "Nov-26", "Dec-26", "Jan-27", "Feb-27", "Mar-27"];
const QUARTER_OF = (mo) => { const i = FY_MONTHS.indexOf(mo); return i < 0 ? "Q1" : `Q${Math.floor(i / 3) + 1}`; };
const VERTICALS = ["ATL/BTL", "Digital", "CX", "ATL", "BTL", "CX-Referral"];
const FIELDS = [
  { k: "leads_actual", l: "Leads (actual)" }, { k: "leads_target", l: "Leads (target)" },
  { k: "qualified_leads_actual", l: "Qualified (actual)" }, { k: "qualified_leads_target", l: "Qualified (target)" },
  { k: "sv_actual", l: "Site Visits (actual)" }, { k: "sv_target", l: "Site Visits (target)" },
  { k: "booking_actual", l: "Bookings (actual)" }, { k: "booking_target", l: "Bookings (target)" },
];

export function FunnelEditorDialog({ onSaved, projects = [] }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState([]);
  const [project, setProject] = useState("");
  const [month, setMonth] = useState("Apr-26");
  const [drafts, setDrafts] = useState({}); // id -> {field: value}
  const [saving, setSaving] = useState(false);
  const [newVert, setNewVert] = useState("Digital");

  const projList = projects && projects.length ? projects : Array.from(new Set(all.map((r) => r.project)));

  const load = () => api.dataListFunnel().then((d) => {
    const rows = d.rows || d || [];
    setAll(rows);
    if (!project && rows.length) setProject(rows[0].project);
  }).catch(() => {});

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const visible = all.filter((r) => r.project === project && r.month === month);
  const valOf = (row, f) => (drafts[row.id] && drafts[row.id][f] !== undefined ? drafts[row.id][f] : (row[f] ?? 0));
  const setVal = (id, f, v) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [f]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const ids = Object.keys(drafts);
      for (const id of ids) {
        const body = {};
        Object.entries(drafts[id]).forEach(([f, v]) => { body[f] = parseFloat(v) || 0; });
        await api.dataUpdateFunnel(id, body);
      }
      toast.success(`Saved ${ids.length || 0} row(s)`);
      setDrafts({});
      await load();
      onSaved && onSaved();
    } catch (e) { toast.error("Save failed — Leader/Admin only"); } finally { setSaving(false); }
  };

  const addRow = async () => {
    if (!project) return;
    try {
      await api.dataCreateFunnel({ project, month, quarter: QUARTER_OF(month), vertical: newVert,
        leads_target: 0, leads_actual: 0, qualified_leads_target: 0, qualified_leads_actual: 0,
        sv_target: 0, sv_actual: 0, booking_target: 0, booking_actual: 0 });
      toast.success("Row added");
      await load();
    } catch (e) { toast.error("Add failed — Leader/Admin only"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="funnel-editor-open"><PencilLine className="mr-1.5 h-3.5 w-3.5" /> Edit funnel</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="funnel-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PencilLine className="h-5 w-5 text-primary" /> Funnel Outcomes</DialogTitle>
          <DialogDescription>Key in leads, qualified leads, site visits and bookings per project / month / vertical. These drive the leads flow and cost ratios.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-[11px]">Project</Label>
            <Select value={project} onValueChange={setProject}>
              <SelectTrigger className="h-9 w-48" data-testid="funnel-editor-project"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>{projList.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-[11px]">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-9 w-32" data-testid="funnel-editor-month"><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent>{FY_MONTHS.map((mo) => <SelectItem key={mo} value={mo}>{mo}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3">
          {visible.length === 0 ? (
            <p className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground">No funnel rows for {project || "—"} · {month}. Add one below.</p>
          ) : visible.map((row) => (
            <div key={row.id} className="rounded-xl border p-3" data-testid={`funnel-editor-row-${row.vertical}`}>
              <p className="mb-2 text-sm font-semibold">{row.vertical}</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {FIELDS.map((f) => (
                  <div key={f.k} className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">{f.l}</Label>
                    <Input type="number" min="0" value={valOf(row, f.k)} onChange={(e) => setVal(row.id, f.k, e.target.value)}
                      className="h-8 text-right tabular-nums" data-testid={`funnel-input-${row.vertical}-${f.k}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex items-end gap-2 rounded-lg border border-dashed p-3">
            <div className="space-y-1"><Label className="text-[11px]">Add vertical</Label>
              <Select value={newVert} onValueChange={setNewVert}>
                <SelectTrigger className="h-9 w-40" data-testid="funnel-editor-new-vertical"><SelectValue /></SelectTrigger>
                <SelectContent>{VERTICALS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={addRow} data-testid="funnel-editor-add"><Plus className="mr-1.5 h-4 w-4" /> Add row</Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving || Object.keys(drafts).length === 0} data-testid="funnel-editor-save">
            <Save className="mr-1.5 h-4 w-4" />{saving ? "Saving…" : `Save ${Object.keys(drafts).length || ""} change(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
